import { useEffect, useRef, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useLetterWriting } from '../contexts/LetterWritingContext'
import { useLetterGeneration } from '../hooks/useLetterGeneration'
import { templateService } from '../services/templateService'
import { patientService } from '../services/patientService'
import { formatPatientName } from '../utils/name'
import { LetterInsertToolbar } from './LetterInsertToolbar'
import './LetterWritingPanel.css'

const DEFAULT_WIDTH = 560
const DEFAULT_HEIGHT = 480
const MIN_WIDTH = 400
const MIN_HEIGHT = 280

export function LetterWritingPanel() {
  const {
    isOpen,
    isMinimized,
    patientId,
    templateId,
    referralId,
    consultationId,
    providerCustomNote,
    closePanel,
    updateNote,
    updateTemplate,
    toggleMinimize,
    expandPanel,
  } = useLetterWriting()

  const noteRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT })
  const resizeStartRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null)

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!panelRef.current) return
    const rect = panelRef.current.getBoundingClientRect()
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      w: rect.width,
      h: rect.height,
    }
    const onMove = (ev: MouseEvent) => {
      const start = resizeStartRef.current!
      const dx = ev.clientX - start.x
      const dy = ev.clientY - start.y
      /* Top-left resize: drag left/up increases size */
      const newWidth = Math.max(MIN_WIDTH, Math.min(window.innerWidth - 40, start.w - dx))
      const newHeight = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - 40, start.h - dy))
      setSize({ width: newWidth, height: newHeight })
    }
    const onUp = () => {
      resizeStartRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'nwse-resize'
    document.body.style.userSelect = 'none'
  }, [])

  const { data: patient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getById(patientId!),
    enabled: !!patientId,
  })

  const { data: allTemplates = [] } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templateService.getAll(),
    enabled: isOpen && !!patientId,
  })

  const selectedTemplate = allTemplates.find((t) => t.id === templateId)

  const { generate, isGenerating } = useLetterGeneration({
    patientId: patientId!,
    templateId: templateId || '',
    consultationId: consultationId || undefined,
    referralId: referralId || undefined,
    providerCustomNote,
    templateName: selectedTemplate?.name,
  })

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isMinimized) {
          expandPanel()
        } else if (providerCustomNote.trim()) {
          if (window.confirm('Discard your letter draft?')) {
            closePanel()
          }
        } else {
          closePanel()
        }
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, isMinimized, providerCustomNote, closePanel, expandPanel])

  const handleInsertBlock = useCallback(
    (text: string) => {
      const textarea = noteRef.current
      if (!textarea) {
        updateNote(providerCustomNote + (providerCustomNote ? '\n\n' : '') + text)
        return
      }
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const before = providerCustomNote.slice(0, start)
      const after = providerCustomNote.slice(end)
      const insert = text.endsWith('\n') ? text : text + '\n\n'
      updateNote(before + insert + after)
      requestAnimationFrame(() => {
        textarea.focus()
        const newPos = start + insert.length
        textarea.setSelectionRange(newPos, newPos)
      })
    },
    [providerCustomNote, updateNote]
  )

  if (!isOpen || !patientId) return null

  const handleDiscard = () => {
    if (providerCustomNote.trim()) {
      if (window.confirm('Discard your letter draft?')) {
        closePanel()
      }
    } else {
      closePanel()
    }
  }

  const handleGenerate = async () => {
    if (!templateId) {
      alert('Please select a template')
      return
    }
    try {
      await generate()
      closePanel()
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to generate document'
      alert(`Error: ${msg}`)
    }
  }

  const patientName = patient ? formatPatientName(patient as any) : 'Loading...'

  return (
    <div
      ref={panelRef}
      className={`letter-writing-panel ${isMinimized ? 'minimized' : ''}`}
      role="dialog"
      aria-label="Letter composer"
      aria-expanded={!isMinimized}
      style={
        !isMinimized
          ? { width: size.width, height: size.height }
          : undefined
      }
    >
      <div
        className="letter-writing-panel-header"
        onClick={isMinimized ? expandPanel : undefined}
      >
        {isMinimized ? (
          <div className="letter-writing-pill">
            <span className="letter-writing-pill-patient">{patientName}</span>
            <span>— Letter in progress</span>
          </div>
        ) : (
          <h2>Letter for {patientName}</h2>
        )}
        <div className="letter-writing-panel-header-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="letter-writing-panel-btn"
            onClick={toggleMinimize}
            aria-label={isMinimized ? 'Expand letter composer' : 'Minimize letter composer'}
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            {isMinimized ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M8 3L12 7H4L8 3zM8 13L4 9h8l-4 4z" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M8 13L4 9h8l-4 4zM8 3L12 7H4l4-4z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="letter-writing-panel-btn"
            onClick={handleDiscard}
            aria-label="Close letter composer"
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div
            className="letter-writing-panel-resize-handle"
            onMouseDown={handleResizeStart}
            role="separator"
            aria-label="Resize letter composer"
          />
          <div className="letter-writing-panel-body">
            <div className="letter-writing-panel-form-group">
              <Link
                to={`/patients/${patientId}`}
                className="letter-writing-panel-patient-link"
                onClick={(e) => e.stopPropagation()}
              >
                View patient record
              </Link>
            </div>

            <div className="letter-writing-panel-form-group">
              <label htmlFor="letter-template">Template</label>
              <select
                id="letter-template"
                value={templateId || ''}
                onChange={(e) => updateTemplate(e.target.value || null)}
                disabled={isGenerating}
              >
                <option value="">Select template</option>
                {allTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="letter-writing-panel-form-group letter-writing-panel-form-group-note">
              <label htmlFor="letter-note">Provider note (included in letter)</label>
              <LetterInsertToolbar
                patient={patient}
                patientId={patientId}
                onInsert={handleInsertBlock}
                disabled={isGenerating}
              />
              <textarea
                ref={noteRef}
                id="letter-note"
                className="letter-writing-panel-note"
                value={providerCustomNote}
                onChange={(e) => updateNote(e.target.value)}
                placeholder="Add your note here. You can browse the patient record while writing—this panel stays open."
                disabled={isGenerating}
                rows={6}
              />
            </div>
          </div>

          <div className="letter-writing-panel-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleDiscard}
              disabled={isGenerating}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleGenerate}
              disabled={isGenerating || !templateId}
            >
              {isGenerating ? 'Generating...' : 'Generate Document'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
